import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { cn } from "src/lib/utils";

/* Blue pill with a spinner: the common "something is loading" format of the app */
export function LoadingPill({ label, className }) {
  return (
    <span className={cn("app-loading-pill", className)}>
      <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

/*
 * Loading state of a section (or of the whole page).
 *   variant "inline":  pill + optional hint, in the page flow (above a skeleton, for example)
 *   variant "tile":    small spinner centered over a media tile (video / stream being loaded)
 *   variant "overlay": blurred backdrop over the whole page that blocks any interaction
 *                      until it is removed (portaled to body, so it also covers the sidebar)
 */
export default function LoadingIndicator({ label = "Loading…", hint, variant = "inline", className }) {
  if (variant === "tile") {
    return (
      <div className={cn("app-loading-tile", className)} role="status" aria-live="polite">
        <Loader2 className="h-7 w-7 animate-spin" aria-hidden="true" />
        {label && <span className="app-loading-tile-label">{label}</span>}
      </div>
    );
  }

  if (variant === "overlay") {
    return <LoadingOverlay label={label} hint={hint} className={className} />;
  }

  return (
    <div className={cn("app-loading-inline", className)} role="status" aria-live="polite">
      <LoadingPill label={label} />
      {hint && <p className="app-loading-hint">{hint}</p>}
    </div>
  );
}

function LoadingOverlay({ label, hint, className }) {
  const overlayRef = useRef(null);

  // Take the focus away from the page (e.g. the switch that was just pressed) and keep it
  // on the overlay, so nothing behind it can be activated with the keyboard either.
  useEffect(() => {
    const previous = document.activeElement;
    overlayRef.current?.focus();
    return () => {
      if (previous && document.contains(previous)) previous.focus?.();
    };
  }, []);

  return createPortal(
    <div
      ref={overlayRef}
      className={cn("app-loading-overlay", className)}
      role="alertdialog"
      aria-modal="true"
      aria-busy="true"
      aria-label={label}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Tab" || event.key === "Enter" || event.key === " ") event.preventDefault();
      }}
    >
      <div className="app-loading-overlay-box" role="status" aria-live="polite">
        <LoadingPill label={label} />
        {hint && <p className="app-loading-hint">{hint}</p>}
      </div>
    </div>,
    document.body
  );
}
